import React, { useState } from 'react';
import { useEnvironment } from '@wix/yoshi-flow-editor';

import { ActionsMenuLayout } from 'wix-ui-tpa/cssVars';
import { Drawer } from '../Drawer';
import { Popover } from '../Popover';
import { Wire } from '../Wire';

import classes from './Menu.scss';

interface IProps {
  isOpen?: boolean;
  controlled?: boolean;

  element: React.ReactElement;
  children: React.ReactNode | React.ReactNode[];

  drawerProps?: Partial<React.ComponentProps<typeof Drawer>>;
  popoverProps?: Partial<React.ComponentProps<typeof Popover>>;

  onClose?(): void;
}

export function Menu(props: IProps) {
  const { controlled, element } = props;
  const { isMobile } = useEnvironment();

  const [isOpen, setIsOpen] = useState(false);

  const Element = controlled
    ? element
    : React.cloneElement(element, {
        ...element.props,
        onClick: handleOpen,
      });

  const children = React.Children.toArray(props.children).filter(
    (child): child is React.ReactElement => React.isValidElement(child),
  );

  const Content = controlled
    ? children
    : children.map((child) => {
        let element: React.ReactElement;

        switch (child.type) {
          case ActionsMenuLayout.Item:
            return React.cloneElement(child, {
              ...child.props,
              onClick: handleChildClick(child.props.onClick),
            });

          case ActionsMenuLayout.Divider:
            return child;

          case Wire:
            element = React.cloneElement(child, {
              ...child.props.children.props,
              onClick: handleChildClick(child.props.children.props.onClick),
            });

            try {
              element.type = ActionsMenuLayout.Item;
            } catch {}

            return element;

          default:
            element = React.cloneElement(child, {
              ...child.props,
              onClick: handleChildClick(child.props.onClick),
            });

            try {
              element.type = ActionsMenuLayout.Item;
            } catch {}

            return element;
        }
      });

  if (isMobile) {
    return (
      <>
        {Element}
        <Drawer
          bw
          anchor="bottom"
          isOpen={controlled ? props.isOpen : isOpen}
          onClose={controlled ? props.onClose! : handleClose}
          {...props.drawerProps}
        >
          <ActionsMenuLayout className={classes.menu}>
            {Content}
          </ActionsMenuLayout>
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      empty
      placement="bottom-end"
      isOpen={controlled ? props.isOpen : isOpen}
      onClose={controlled ? props.onClose! : handleClose}
      content={
        <ActionsMenuLayout className={classes.menu}>
          {Content}
        </ActionsMenuLayout>
      }
      {...props.popoverProps}
    >
      {Element}
    </Popover>
  );

  function handleChildClick(onClick?: Function) {
    return function (event: React.MouseEvent<HTMLButtonElement>) {
      onClick?.(event);
      handleClose();
    };
  }

  function handleOpen() {
    setIsOpen(true);
    element.props.onClick?.();
  }

  function handleClose() {
    setIsOpen(false);
  }
}

Menu.displayName = 'wui/Menu';

export const MenuItem = ActionsMenuLayout.Item;
export const MenuDivider = ActionsMenuLayout.Divider;
